import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { ArrowRight, FilePlus2, Search } from "lucide-react";

const statusColor = {
  Draft: "bg-slate-100 text-slate-700",
  Sent: "bg-blue-100 text-blue-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-rose-100 text-rose-700",
  Paid: "bg-orange-100 text-orange-700",
};

export default function SavedQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/quotes").then((r) => setQuotes(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = quotes.filter((x) => {
    const hay = `${x.generated?.project_title} ${x.customer?.name} ${x.customer?.address} ${x.quote_type}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="max-w-5xl mx-auto" data-testid="saved-quotes-page">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Saved Quotes</h1>
          <p className="text-slate-500 mt-1">All your quotes in one place.</p>
        </div>
        <Link to="/app/new"><Button className="bg-orange-500 hover:bg-orange-600 text-white"><FilePlus2 className="h-4 w-4 mr-2" />New Quote</Button></Link>
      </div>

      <div className="mb-5 relative max-w-md">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by project, customer, address…" className="pl-9" data-testid="quotes-search-input" />
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400 border-slate-200">Loading…</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-slate-300 bg-white">
          <p className="text-slate-500">No quotes yet.</p>
        </Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <div className="divide-y divide-slate-100">
            {filtered.map((x) => (
              <Link key={x.id} to={`/app/quotes/${x.id}`} data-testid={`saved-quote-row-${x.id}`} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-900 truncate">{x.generated?.project_title || "Untitled"}</div>
                    <Badge className={`${statusColor[x.status] || statusColor.Draft} hover:opacity-90`}>{x.status}</Badge>
                    <Badge variant="outline" className="border-slate-200 text-slate-600">{x.quote_type}</Badge>
                  </div>
                  <div className="text-sm text-slate-500 mt-1 truncate">
                    {x.customer?.name || "—"} · {x.customer?.address || "No address"} · {new Date(x.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="font-display font-semibold text-slate-900">${Number(x.total_price || 0).toLocaleString()}</div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
